import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { BarChart3, Download, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Inline form for the "Post Event Report" tile.
 *
 * - Toggles whether to apply refunds for scratched entries (on by
 *   default per owner spec).
 * - Lets the user add free-form expense rows (description + amount).
 * - Generates a ZIP with both the .xlsx (native charts) and .pdf
 *   (matplotlib charts embedded in a Word doc → LibreOffice PDF).
 *
 * Mirrors the volunteers UX from the Final Programme panel: text
 * input row at the top → Add → accumulated rows below with a
 * trash icon on each row.
 */
export default function PostEventReportPanel({ competitionId }) {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applyRefunds, setApplyRefunds] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [newDesc, setNewDesc] = useState('');
  const [newAmt, setNewAmt] = useState('');

  // Load preview whenever the comp or the refunds toggle changes —
  // the figures shown on screen always match what the file will
  // contain.
  useEffect(() => {
    if (!competitionId) {
      setPreview(null);
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const res = await axios.get(
          `${API}/post-event-report/${competitionId}/preview`,
          { params: { apply_refunds: applyRefunds }, withCredentials: true }
        );
        setPreview(res.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load report preview');
        setPreview(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [competitionId, applyRefunds]);

  // Load any previously-persisted expenses + apply_refunds for this
  // competition on first mount / comp change. Owner-requested
  // persistence (Feb 2026) so PER inputs survive across sessions and
  // are reusable by the Competition Summary Pack.
  useEffect(() => {
    if (!competitionId) {
      setExpenses([]);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(
          `${API}/post-event-report/${competitionId}/expenses`,
          { withCredentials: true }
        );
        setExpenses(res.data?.expenses || []);
        if (res.data?.has_persisted) {
          setApplyRefunds(res.data.apply_refunds);
        }
      } catch (err) {
        // Non-fatal — just start with empty expenses.
      }
    })();
  }, [competitionId]);

  const persistExpenses = async (next) => {
    if (!competitionId) return;
    try {
      await axios.put(
        `${API}/post-event-report/${competitionId}/expenses`,
        { expenses: next, apply_refunds: applyRefunds },
        { withCredentials: true }
      );
    } catch (err) {
      // Non-fatal — surface so the user knows it didn't stick.
      toast.warning('Expense saved locally — could not persist to server');
    }
  };

  const addExpense = () => {
    const desc = newDesc.trim();
    const amt = parseFloat(newAmt);
    if (!desc) {
      toast.warning('Description is required');
      return;
    }
    if (Number.isNaN(amt) || amt < 0) {
      toast.warning('Amount must be a non-negative number');
      return;
    }
    const next = [...expenses, { description: desc, amount: amt }];
    setExpenses(next);
    setNewDesc('');
    setNewAmt('');
    persistExpenses(next);
  };

  const removeExpense = (i) => {
    const next = expenses.filter((_, idx) => idx !== i);
    setExpenses(next);
    persistExpenses(next);
  };

  const moneyFmt = (n) =>
    `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const totalExpenses = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);

  const generate = async () => {
    if (!competitionId) return;
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/post-event-report/${competitionId}/generate`,
        { apply_refunds: applyRefunds, expenses },
        { withCredentials: true, responseType: 'blob', timeout: 180000 }
      );
      // Trigger a browser download for the ZIP
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : 'Post_Event_Report.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report ZIP downloaded (contains both .xlsx and .pdf)');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Generate failed');
    } finally {
      setGenerating(false);
    }
  };

  if (!competitionId) {
    return (
      <div className="text-sm text-stone-600">
        Select a competition above to generate the post-event report.
      </div>
    );
  }

  return (
    <div className="space-y-6 max-h-[calc(100vh-280px)] overflow-y-auto pr-2"
         data-testid="post-event-report-panel">
      <div>
        <h3 className="text-lg font-semibold text-stone-800 mb-1 flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-[#5C4033]" />
          Post Event Report
        </h3>
        <p className="text-sm text-muted-foreground">
          Key insights from the selected competition — generates an Excel
          workbook (with editable charts) and a PDF (dashboard view) in a single
          download.
        </p>
      </div>

      {/* Preview tiles — kept in sync with the refunds toggle */}
      {loading ? (
        <p className="text-sm text-stone-600" data-testid="post-event-report-loading">
          Loading preview…
        </p>
      ) : preview ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3"
             data-testid="post-event-report-kpis">
          {[
            ['Individuals entered', preview.headline.individuals],
            ['Pairs-only skaters', preview.headline.pairs_only],
            ['Team-only skaters', preview.headline.team_only],
            ['Total event entries', preview.headline.total_event_entries],
            ['Gross entry revenue', moneyFmt(preview.headline.gross_revenue)],
          ].map(([label, value]) => (
            <div key={label}
                 className="rounded-lg border border-[#A88A75] bg-[#FAF7F2] px-3 py-3 text-center">
              <div className="text-2xl font-bold text-[#5C4033]">{value}</div>
              <div className="text-xs text-stone-600 mt-0.5">{label}</div>
            </div>
          ))}
        </div>
      ) : null}

      {preview?.headline?.total_refunds > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
             data-testid="post-event-report-refunds-line">
          Refunds applied: <strong>−{moneyFmt(preview.headline.total_refunds)}</strong> across
          {' '}scratched entries (already netted into Gross revenue above).
        </div>
      )}

      {/* Refunds toggle */}
      <div className="flex items-center justify-between rounded-lg border bg-stone-50 px-3 py-3">
        <div>
          <Label htmlFor="apply-refunds-switch" className="font-medium">
            Apply refunds for scratched entries
          </Label>
          <p className="text-xs text-stone-600 mt-0.5">
            When on, scratched entries are removed before the fee is recalculated.
            A skater who scratches all entries gets a full refund; a partial scratch
            may drop them below their tier cap.
          </p>
        </div>
        <Switch
          id="apply-refunds-switch"
          checked={applyRefunds}
          onCheckedChange={setApplyRefunds}
          data-testid="post-event-report-apply-refunds-switch"
        />
      </div>

      {/* Expenses entry */}
      <div className="rounded-lg border p-4 space-y-3">
        <div>
          <h4 className="font-semibold text-stone-800">Expenses (optional)</h4>
          <p className="text-xs text-stone-600">
            Add any event-related expenses to be subtracted from the gross revenue
            for the net-revenue line on the report.
          </p>
        </div>
        <div className="flex flex-col md:flex-row gap-2">
          <Input
            type="text"
            placeholder="Description (e.g. Floor hire)"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            className="flex-1"
            data-testid="post-event-report-expense-desc-input"
          />
          <Input
            type="number"
            step="0.01"
            min="0"
            placeholder="Amount"
            value={newAmt}
            onChange={(e) => setNewAmt(e.target.value)}
            className="w-40"
            data-testid="post-event-report-expense-amount-input"
          />
          <Button
            type="button"
            onClick={addExpense}
            className="bg-[#5C4033] hover:bg-[#3d2a22] text-white"
            data-testid="post-event-report-expense-add-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>

        {expenses.length > 0 && (
          <table className="w-full text-sm border-collapse" data-testid="post-event-report-expense-table">
            <thead>
              <tr className="bg-stone-100">
                <th className="border px-3 py-2 text-left">Description</th>
                <th className="border px-3 py-2 text-right w-32">Amount</th>
                <th className="border px-3 py-2 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((e, i) => (
                <tr key={i} data-testid={`post-event-report-expense-row-${i}`}>
                  <td className="border px-3 py-2">{e.description}</td>
                  <td className="border px-3 py-2 text-right">{moneyFmt(e.amount)}</td>
                  <td className="border px-3 py-2 text-center">
                    <button
                      type="button"
                      onClick={() => removeExpense(i)}
                      className="text-red-600 hover:text-red-800"
                      data-testid={`post-event-report-expense-delete-${i}`}
                      aria-label={`Remove expense ${e.description}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
              <tr className="bg-stone-50 font-semibold">
                <td className="border px-3 py-2 text-right">Total expenses</td>
                <td className="border px-3 py-2 text-right text-[#5C4033]">
                  {moneyFmt(totalExpenses)}
                </td>
                <td className="border"></td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      <Button
        onClick={generate}
        disabled={generating || !competitionId}
        className="bg-[#5C4033] hover:bg-[#3d2a22] text-white w-full"
        data-testid="post-event-report-generate-btn"
      >
        {generating
          ? <><BarChart3 className="h-4 w-4 mr-2 animate-pulse" /> Generating…</>
          : <><Download className="h-4 w-4 mr-2" /> Generate Post Event Report (.xlsx + .pdf)</>}
      </Button>
    </div>
  );
}
